import { CalculationMethod, CalculationParameters } from "adhan";
import type { PrayerMethodConfig, PrayerMethodOption, PrayerMethodValue } from "@/types/prayer";

type PrayerMethodFactoryKey = Exclude<PrayerMethodValue, "Other">;

const defaultPrayerMethodConfig: PrayerMethodConfig = {
  method: "Other",
  fajrAngle: 12,
  ishaAngle: 12,
  ishaInterval: 0,
};

const methodFactories: Record<PrayerMethodFactoryKey, () => CalculationParameters> = {
  Dubai: CalculationMethod.Dubai,
  Egyptian: CalculationMethod.Egyptian,
  Karachi: CalculationMethod.Karachi,
  Kuwait: CalculationMethod.Kuwait,
  MoonsightingCommittee: CalculationMethod.MoonsightingCommittee,
  MuslimWorldLeague: CalculationMethod.MuslimWorldLeague,
  NorthAmerica: CalculationMethod.NorthAmerica,
  Qatar: CalculationMethod.Qatar,
  Singapore: CalculationMethod.Singapore,
  Turkey: CalculationMethod.Turkey,
  UmmAlQura: CalculationMethod.UmmAlQura,
};

export const calculationMethodOptions = [
  { label: "Nautical Twilight (12°, 12°)", value: "Other" },
  { label: "Muslim World League (18°, 17°)", value: "MuslimWorldLeague" },
  { label: "Egyptian General Authority (19.5°, 17.5°)", value: "Egyptian" },
  { label: "Karachi - University of Islamic Sciences (18°, 18°)", value: "Karachi" },
  { label: "Umm al-Qura - Makkah (18.5°, 90 min)", value: "UmmAlQura" },
  { label: "Dubai (18.2°, 18.2°)", value: "Dubai" },
  { label: "Moonsighting Committee Worldwide (18°, 18°)", value: "MoonsightingCommittee" },
  { label: "North America - ISNA (15°, 15°)", value: "NorthAmerica" },
  { label: "Kuwait (18°, 17.5°)", value: "Kuwait" },
  { label: "Qatar (18°, 90 min)", value: "Qatar" },
  { label: "Singapore (20°, 18°)", value: "Singapore" },
  { label: "Turkey - Diyanet (18°, 17°)", value: "Turkey" },
] as const satisfies readonly PrayerMethodOption[];

const buildMethodConfig = (method: PrayerMethodValue): PrayerMethodConfig => {
  if (method === "Other") {
    return defaultPrayerMethodConfig;
  }

  const parameters = methodFactories[method]();

  return {
    fajrAngle: parameters.fajrAngle,
    ishaAngle: parameters.ishaAngle,
    ishaInterval: parameters.ishaInterval ?? 0,
    method,
  };
};

export const methodPresets = Object.fromEntries(
  calculationMethodOptions.map((option) => [option.value, buildMethodConfig(option.value)]),
) as Record<PrayerMethodValue, PrayerMethodConfig>;

export const methodLabelMap = Object.fromEntries(
  calculationMethodOptions.map((option) => [option.value, option.label]),
) as Record<PrayerMethodValue, string>;

const formatPresetNumber = (value: number) => (Number.isInteger(value) ? value.toString() : value.toFixed(1));

export const prayerMethodConfig = defaultPrayerMethodConfig;

export const getPrayerMethodConfig = (method: PrayerMethodValue) => methodPresets[method] ?? defaultPrayerMethodConfig;

export const getPrayerMethodLabel = (method: PrayerMethodValue) => methodLabelMap[method] ?? methodLabelMap.Other;

export const formatPrayerMethodSummary = ({ fajrAngle, ishaAngle, ishaInterval }: PrayerMethodConfig) =>
  ishaInterval > 0
    ? `${formatPresetNumber(fajrAngle)}° Fajr · ${formatPresetNumber(ishaInterval)} min Isha`
    : `${formatPresetNumber(fajrAngle)}° Fajr · ${formatPresetNumber(ishaAngle)}° Isha`;

export const createCalculationParameters = (config: PrayerMethodConfig) => {
  const parameters =
    config.method === "Other"
      ? new CalculationParameters("Other", config.fajrAngle, config.ishaAngle)
      : methodFactories[config.method]();

  parameters.fajrAngle = config.fajrAngle;
  parameters.ishaAngle = config.ishaAngle;
  parameters.ishaInterval = config.ishaInterval;

  return parameters;
};
